import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, Grid, Paper, Typography, TextField, Button, 
    IconButton, Stack, Divider, InputAdornment, 
    useTheme, useMediaQuery, Drawer, Fab, Badge, CircularProgress,
    Chip
} from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faSearch, faTrashAlt, faMoneyBillWave, faTags, 
    faPlus, faMinus, faUserXmark, faShoppingCart, faTimes
} from "@fortawesome/free-solid-svg-icons";

// Componentes y Controladores
import InlineEntityFinder from '../../../components/entities/InlineEntityFinder';
import CreateEntityModal from '../../../components/entities/CreateEntityModal';
import UserContext from '../../../navigation/context/userContext';
import { initPos, searchPosProducts } from '../../../controllers/company/pos/PosCatalogController';

export default function PosDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    const { userData } = useContext(UserContext);
    const [isLoading, setIsLoading] = useState(true);

    // --- ESTADOS PRINCIPALES ---
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [exchangeRate, setExchangeRate] = useState(1);
    const [countryId, setCountryId] = useState(null);
    
    // --- ESTADOS DEL CLIENTE ---
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCreateClient, setShowCreateClient] = useState(false);

    // --- CARGA INICIAL ---
    useEffect(() => {
        handleInitPos();
    }, []);

    const getActiveMerchantUuid = () => {
        return localStorage.getItem('work_mode_merchant_id') || userData?.entity?.uuid || null;
    };

    const handleInitPos = async () => {
        setIsLoading(true);
        const merchantUuid = getActiveMerchantUuid();
        
        if (merchantUuid) {
            const response = await initPos(merchantUuid);
            setCategories(response.categories);
            setProducts(response.top_products);
            setExchangeRate(response.exchange_rate || 1);
            setCountryId(response.country_id);
        }
        setIsLoading(false);
    };

    const handleCategoryClick = async (categoryId) => {
        setIsLoading(true);
        const merchantUuid = getActiveMerchantUuid();
        
        // Si el usuario hace clic en "Top Vendidos" (null), volvemos a llamar a init, 
        // si es otra categoría, llamamos a search
        if (categoryId === null) {
            handleInitPos();
        } else {
            const filteredProducts = await searchPosProducts(merchantUuid, '', categoryId);
            setProducts(filteredProducts);
        }
        setIsLoading(false);
    };

    // --- ACCIONES DEL CARRITO ---
    const handleProductClick = (product) => {
        if (product.has_variants) {
            console.log("Variations modal trigger:", product.name);
        } else {
            const existingItem = cart.find(item => item.id === product.id);
            if (existingItem) {
                updateQuantity(product.id, parseFloat(existingItem.quantity) + 1);
            } else {
                setCart([...cart, { ...product, quantity: 1 }]);
            }
        }
    };

    const updateQuantity = (id, value) => {
        if (value === '') {
            setCart(cart.map(item => item.id === id ? { ...item, quantity: '' } : item));
            return;
        }
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue) || parsedValue < 0) return;
        setCart(cart.map(item => item.id === id ? { ...item, quantity: parsedValue } : item));
    };

    const removeLine = (id) => setCart(cart.filter(item => item.id !== id));

    // --- CÁLCULOS ---
    const subtotal = cart.reduce((acc, item) => acc + (item.price * (parseFloat(item.quantity) || 0)), 0);
    const totalItems = cart.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
    const total = subtotal;

    // --- UI DEL CARRITO ---
    const cartUI = (
        <Paper elevation={isMobile ? 0 : 6} sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: isMobile ? 0 : 1, overflow: 'hidden' }}>
            {isMobile && (
                <Box sx={{ p: 2, bgcolor: '#0F172A', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">Checkout</Typography>
                    <IconButton size="small" sx={{ color: 'white' }} onClick={() => setMobileCartOpen(false)}><FontAwesomeIcon icon={faTimes}/></IconButton>
                </Box>
            )}

            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight="700" mb={1}>CUSTOMER</Typography>
                {!selectedCustomer ? (
                    <InlineEntityFinder onSelectEntity={setSelectedCustomer} onAddNew={() => setShowCreateClient(true)} />
                ) : (
                    <Box sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 1, position: 'relative' }}>
                        <Typography variant="body2" fontWeight="700">{selectedCustomer.name}</Typography>
                        <IconButton size="small" color="error" onClick={() => setSelectedCustomer(null)} sx={{ position: 'absolute', right: 5, top: 5 }}><FontAwesomeIcon icon={faUserXmark} size="xs"/></IconButton>
                    </Box>
                )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                <Stack spacing={2} divider={<Divider variant="middle"/>}>
                    {cart.map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="700" noWrap>{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary">${item.price.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => updateQuantity(item.id, Math.max(0, (parseFloat(item.quantity) || 0) - 1))}><FontAwesomeIcon icon={faMinus} size="xs"/></IconButton>
                                <TextField value={item.quantity} onChange={(e) => updateQuantity(item.id, e.target.value)} size="small" inputProps={{ style: { textAlign: 'center', width: '40px' } }} />
                                <IconButton size="small" onClick={() => updateQuantity(item.id, (parseFloat(item.quantity) || 0) + 1)}><FontAwesomeIcon icon={faPlus} size="xs"/></IconButton>
                            </Box>
                            <Typography variant="body2" fontWeight="700" sx={{ minWidth: 50, textAlign: 'right' }}>${(item.price * item.quantity).toFixed(2)}</Typography>
                            <IconButton size="small" color="error" onClick={() => removeLine(item.id)}><FontAwesomeIcon icon={faTrashAlt} size="sm"/></IconButton>
                        </Box>
                    ))}
                </Stack>
            </Box>

            <Box sx={{ p: 2, bgcolor: '#0F172A', color: 'white' }}>
                <Stack direction="row" justifyContent="space-between" mb={1}><Typography variant="body2" color="#94a3b8">Subtotal</Typography><Typography variant="body1" fontWeight="700">${subtotal.toFixed(2)}</Typography></Stack>
                {countryId === 1 && (
                    <Stack direction="row" justifyContent="space-between" mb={1}><Typography variant="caption" color="#4ade80">Bs. Ref: {(total * exchangeRate).toFixed(2)}</Typography></Stack>
                )}
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Typography variant="h6" mb={2}>Total: <span style={{ color: '#38bdf8' }}>${total.toFixed(2)}</span></Typography>
                <Button fullWidth variant="contained" disabled={cart.length === 0 || !selectedCustomer} sx={{ py: 1.5 }}>Pay Now</Button>
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f1f5f9', p: isMobile ? 1 : 2 }}>
            <CreateEntityModal open={showCreateClient} onClose={() => setShowCreateClient(false)} onSuccess={(e) => { setSelectedCustomer(e); setShowCreateClient(false); }} />
            
            {isLoading ? (
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} sx={{ height: '100%' }}>
                    <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Paper sx={{ p: 2, mb: 2 }}><TextField fullWidth placeholder="Search products..." InputProps={{ startAdornment: <InputAdornment position="start"><FontAwesomeIcon icon={faSearch}/></InputAdornment> }} /></Paper>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 1 }}>
                            {/* Chip para "Top Vendidos" */}
                            <Chip 
                                label="Top Vendidos"
                                onClick={() => {
                                    setSelectedCategory(null);
                                    handleCategoryClick(null);
                                }}
                                color={selectedCategory === null ? "primary" : "default"}
                                variant={selectedCategory === null ? "filled" : "outlined"}
                                sx={{ 
                                    minWidth: '120px', 
                                    borderRadius: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            />
                            
                            {/* Chips de Categorías */}
                            {categories.map(c => (
                                <Chip 
                                    key={c.id} 
                                    label={c.name}
                                    onClick={() => {
                                        setSelectedCategory(c.id);
                                        handleCategoryClick(c.id);
                                    }}
                                    color={selectedCategory === c.id ? "primary" : "default"}
                                    variant={selectedCategory === c.id ? "filled" : "outlined"}
                                    sx={{ 
                                        minWidth: '120px', 
                                        borderRadius: '16px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer'
                                    }}
                                />
                            ))}
                        </Stack>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <Grid container spacing={2}>
                                {products.map(p => (
                                    <Grid item xs={6} sm={4} md={3} key={p.id}>
                                        <Paper sx={{ p: 2, textAlign: 'center', cursor: 'pointer' }} onClick={() => handleProductClick(p)}>
                                            <Typography variant="caption" color="primary">{p.category}</Typography>
                                            <Typography variant="subtitle2" fontWeight="700">{p.name}</Typography>
                                            <Typography variant="h6" color="#0284c7" fontWeight="900">${p.price}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                    {!isMobile && <Grid item md={5} lg={4}>{cartUI}</Grid>}
                </Grid>
            )}

            {isMobile && (
                <>
                    <Fab color="primary" sx={{ position: 'absolute', bottom: 20, right: 20 }} onClick={() => setMobileCartOpen(true)}><Badge badgeContent={totalItems} color="error"><FontAwesomeIcon icon={faShoppingCart}/></Badge></Fab>
                    <Drawer anchor="right" open={mobileCartOpen} onClose={() => setMobileCartOpen(false)} PaperProps={{ sx: { width: '100%' } }}>{cartUI}</Drawer>
                </>
            )}
        </Box>
    );
}